import * as React from 'react';
import { ReactNode, createContext } from 'react';

import { useMutation } from '@tanstack/react-query';

import { useQuizQuestionUrl } from '~teacher/components/QuizEditor/contexts/QuizQuestionUrlProvider';
import { removeQuestion } from '~teacher/slices/quiz/quizSlice';

import {
  CreateBody,
  DeleteBulkQuestionsParams,
  QuizQuestionUrl,
  deleteBulkQuestions,
} from '../../../../api/question';

export const UseBulkDeleteQuestionContext =
  createContext<ReturnType<typeof useBulkDeleteQuestion>>(undefined);

interface Props<TBodyData, TReturnType> {
  createBody: CreateBody<TBodyData>;
  onMutate?: OnMutate;
  onSuccess?: OnSuccess<TReturnType, Params<TBodyData>>;
  children: ReactNode;
}
export function UseBulkDeleteQuestionProvider<TBodyData, TReturnType>({
  children,
  createBody,
  onMutate,
  onSuccess,
}: Props<TBodyData, TReturnType>) {
  const quizQuestionUrl = useQuizQuestionUrl();
  const mutation = useBulkDeleteQuestion({
    url: quizQuestionUrl,
    onSuccess: onSuccess,
    createBodyData: createBody,
    onMutate: onMutate,
  });
  return (
    <UseBulkDeleteQuestionContext.Provider value={mutation}>
      {children}
    </UseBulkDeleteQuestionContext.Provider>
  );
}

export function useProvideBulkQuestionDelete() {
  const value = React.useContext(UseBulkDeleteQuestionContext);
  if (value === undefined) {
    throw new Error(
      'UseBulkDeleteQuestionContext is not defined. Remember to use a UseBulkDeleteQuestionProvider'
    );
  }
  return value;
}

export type OnMutate = () => void;
export type OnSuccess<TReturnType, TParamType> = (
  returnData: TReturnType,
  params: Params<TParamType>
) => void;

interface UseBulkDeleteQuestionParams<TBodyData, TReturnType> {
  createBodyData: CreateBody<TBodyData>;
  onMutate?: OnMutate;
  onSuccess?: OnSuccess<TReturnType, Params<TBodyData>>;
  url: QuizQuestionUrl;
}

export type Params<TBodyData> = Pick<
  DeleteBulkQuestionsParams<TBodyData>,
  'questionIds' | 'quizId'
>;
export function useBulkDeleteQuestion<TBodyData, TReturnType>({
  createBodyData,
  onMutate,
  onSuccess,
  url,
}: UseBulkDeleteQuestionParams<TBodyData, TReturnType>) {
  return useMutation({
    mutationFn: ({
      quizId,
      questionIds,
    }: Pick<
      DeleteBulkQuestionsParams<TBodyData>,
      'questionIds' | 'quizId'
    >) => {
      onMutate?.();

      // Do not delete temp Ids, that will cause a 404 error and delete nothing.
      const toDelete = questionIds.filter(
        (questionId) => !isTempQuestionId(`${questionId}`)
      );

      if (toDelete.length === 0) {
        return;
      }

      return deleteBulkQuestions<TBodyData, TReturnType>({
        quizId,
        questionIds: toDelete,
        createBodyData: createBodyData,
        questionsUrl: url,
      });
    },
    onSuccess: (response, params) => {
      onSuccess?.(response, params);
    },
    onSettled: () => {},
  });
}

/**
 * Check to see if a question id has a temp ID.
 * Temp IDs start with `edit-`
 * @param questionId
 */
export function isTempQuestionId(questionId: string) {
  return /edit/i.test(questionId);
}
